import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = [ "idle", "progress", "support", "fileInput", "submitControl", "busy" ];

  static values = {
    presentation: { type: String, default: "button" },
    fileSingularLabel: { type: String, default: "file" },
    filePluralLabel: { type: String, default: "files" }
  };

  connect() {
    this.submitting = false;
    this.submitControlStates = [];
    this.submitControlDimensions = [];
    this.updateBusyTargets(false);
  }

  start(event) {
    if (event.target !== this.element || this.submitting) return;

    const submitter = event.detail?.formSubmission?.submitter;

    this.beginSubmission(submitter);
  }

  startNative(event) {
    if (event.target !== this.element || event.defaultPrevented) return;

    if (this.submitting) {
      event.preventDefault();
      return;
    }

    this.beginSubmission(event.submitter);
  }

  beginSubmission(submitter) {
    this.submitting = true;
    this.updateBusyTargets(true);
    this.preserveButtonDimensions();
    this.disableSubmitControls(submitter);
    this.showProgress();
  }

  preventDuplicate(event) {
    if (this.submitting) event.preventDefault();
  }

  end(event) {
    if (event.detail?.success) return;

    this.reset();
  }

  reset() {
    if (!this.submitting) return;

    this.submitting = false;
    this.updateBusyTargets(false);
    this.restoreSubmitControls();
    this.restoreButtonDimensions();
    this.hideProgress();
  }

  showProgress() {
    this.idleTargets.forEach((target) => {
      target.hidden = true;
    });
    this.progressTargets.forEach((target) => {
      target.hidden = false;
    });

    if (this.presentationValue === "form") this.updateFileSupport();
  }

  hideProgress() {
    this.progressTargets.forEach((target) => {
      target.hidden = true;
    });
    this.idleTargets.forEach((target) => {
      target.hidden = false;
    });

    if (this.hasSupportTarget) {
      this.supportTarget.hidden = true;
      this.supportTarget.textContent = "";
    }
  }

  updateFileSupport() {
    if (!this.hasSupportTarget) return;

    const fileCount = this.fileInputTargets.filter((input) => !input.disabled).reduce(
      (total, input) => total + (input.files?.length || 0),
      0
    );

    if (fileCount === 0) {
      this.supportTarget.hidden = true;
      this.supportTarget.textContent = "";
      return;
    }

    const fileLabel = fileCount === 1 ? this.fileSingularLabelValue : this.filePluralLabelValue;
    this.supportTarget.textContent = `Uploading ${fileCount} ${fileLabel}. Keep this page open.`;
    this.supportTarget.hidden = false;
  }

  updateBusyTargets(busy) {
    this.busyTargets.forEach((target) => {
      target.setAttribute("aria-busy", busy.toString());
    });
  }

  disableSubmitControls(submitter) {
    this.submitControlStates = this.submitControls()
      .filter((control) => control !== submitter)
      .map((control) => ({
        control,
        disabled: control.disabled
      }));

    this.submitControlStates.forEach(({ control }) => {
      control.disabled = true;
    });
  }

  restoreSubmitControls() {
    this.submitControlStates.forEach(({ control, disabled }) => {
      if (control.isConnected) control.disabled = disabled;
    });
    this.submitControlStates = [];
  }

  submitControls() {
    const formSubmitControls = Array.from(
      this.element.querySelectorAll("button[type='submit'], input[type='submit']")
    );

    return Array.from(new Set([ ...this.submitControlTargets, ...formSubmitControls ]));
  }

  preserveButtonDimensions() {
    if (this.presentationValue !== "button") return;

    this.submitControlDimensions = this.submitControls().map((control) => {
      const bounds = control.getBoundingClientRect();
      const dimensions = {
        control,
        inlineSize: control.style.inlineSize,
        blockSize: control.style.blockSize
      };

      control.style.inlineSize = `${bounds.width}px`;
      control.style.blockSize = `${bounds.height}px`;
      return dimensions;
    });
  }

  restoreButtonDimensions() {
    this.submitControlDimensions.forEach(({ control, inlineSize, blockSize }) => {
      if (!control.isConnected) return;

      control.style.inlineSize = inlineSize;
      control.style.blockSize = blockSize;
    });
    this.submitControlDimensions = [];
  }
}
